// Field-level last-writer-wins, the server's own rule (sync.md §6). Every component is a max over a
// total order (HLC strings), so applying the same mutations in any order or batching gives the same
// entity. The browser uses it twice: to apply a local change at once, and to lay queued local changes
// over a pulled entity so pending edits keep showing (sync.md §5).
import type { Collection, LocalEntity, Mutation, ServerEntity } from "./types";

const max = (a: string | null, b: string | null) => (a === null ? b : b === null ? a : a > b ? a : b);

export const isDeleted = (live: string | null, del: string | null) => del !== null && (live === null || del > live);

export function emptyEntity(collection: Collection, id: string): LocalEntity {
  return { collection, id, fields: {}, hlcs: {}, live: null, del: null, deleted: true, touched: 0 };
}

/** Apply one mutation to an entity, returning a new entity (the input is not changed). */
export function apply(entity: LocalEntity, m: Pick<Mutation, "op" | "fields" | "hlc">, now = Date.now()): LocalEntity {
  const e: LocalEntity = { ...entity, fields: { ...entity.fields }, hlcs: { ...entity.hlcs } };
  if (m.op === "upsert") {
    if (e.del !== null && m.hlc <= e.del) return entity; // written before the delete
    e.live = max(e.live, m.hlc);
    for (const [f, v] of Object.entries(m.fields ?? {})) {
      const h = e.hlcs[f];
      if (h === undefined || m.hlc > h) {
        e.fields[f] = v;
        e.hlcs[f] = m.hlc;
      }
    }
  } else {
    if (e.del !== null && m.hlc <= e.del) return entity;
    e.del = m.hlc;
    for (const [f, h] of Object.entries(e.hlcs)) {
      if (h < m.hlc) {
        delete e.fields[f];
        delete e.hlcs[f];
      }
    }
  }
  e.deleted = isDeleted(e.live, e.del);
  e.touched = now;
  return e;
}

/** A pulled entity as a local one (the server's copy is authoritative). */
export function fromServer(s: ServerEntity): LocalEntity {
  return {
    collection: s.collection,
    id: s.id,
    fields: { ...s.fields },
    hlcs: { ...s.hlcs },
    live: s.liveHlc,
    del: s.deleteHlc,
    deleted: s.deleted,
    touched: s.updatedAt,
  };
}

/** The server's entity with the queued local mutations for it applied on top, in queue order. */
export function rebase(server: ServerEntity, queued: Pick<Mutation, "op" | "fields" | "hlc">[]): LocalEntity {
  let e = fromServer(server);
  for (const m of queued) e = apply(e, m, Math.max(e.touched, server.updatedAt));
  return e;
}

/**
 * Compaction (sync.md §4.5): fold a new upsert into a queued, unsent upsert of the same entity. Per
 * field the greater HLC wins, and the merged mutation takes the greater HLC; that equals applying
 * both in turn. Returns null when they can't be folded (a delete is involved).
 */
export function fold(queued: Mutation, next: Mutation): Mutation | null {
  if (queued.op !== "upsert" || next.op !== "upsert") return null;
  const newer = next.hlc > queued.hlc;
  const fields = newer ? { ...queued.fields, ...next.fields } : { ...next.fields, ...queued.fields };
  return { ...queued, fields, hlc: newer ? next.hlc : queued.hlc };
}
